import React from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { Box, Text, CardRow, Card, Button } from '@/presentation/components'
import { isPrintingServiceOrderDetailState, serviceOrderDetailState } from '@/presentation/pages/ServiceOrderDetail/components'
import { theme } from '@/presentation/styles'
import { useMediaQueries } from '@/presentation/hooks'
import { styles } from '../../styles'
import { selectedOsImageViewState } from '../OsImageView/atom'
import { ServiceOrderImageModel } from '@/domain/models'
import { MaterialCommunityIcons } from '@expo/vector-icons'

export const OsImages: React.FC = () => {
  const { isMobile } = useMediaQueries()
  const serviceOrderDetails = useRecoilValue(serviceOrderDetailState)
  const setImageView = useSetRecoilState(selectedOsImageViewState)
  const isPrinting = useRecoilValue(isPrintingServiceOrderDetailState)

  const handleDownloadFile = (url: string, fileName: string): void => {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.rel = 'noopener'
    link.click()
  }

  const getAttachmentType = (attachment: ServiceOrderImageModel): 'imagem' | 'video' | 'audio' | 'documento' => {
    if (attachment.tipoArquivo) return attachment.tipoArquivo

    const extension = attachment.nomeArquivo.split('.').pop()?.toLowerCase()
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension ?? '')) return 'imagem'
    if (['mp4', 'mov', 'avi', 'mkv', 'wmv', 'webm', 'm4v'].includes(extension ?? '')) return 'video'
    if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(extension ?? '')) return 'audio'
    return 'documento'
  }

  const canPreviewAttachment = (attachment: ServiceOrderImageModel, type: ReturnType<typeof getAttachmentType>): boolean => {
    if (type === 'imagem') return true

    if (attachment.caminhoVisualizacao) return true
    const extension = attachment.nomeArquivo.split('.').pop()?.toLowerCase()
    return (type === 'video' && extension === 'mp4') || (type === 'audio' && extension === 'mp3')
  }

  const getAttachmentPresentation = (type: ReturnType<typeof getAttachmentType>): {
    label: string
    icon: 'video-outline' | 'music-note-outline' | 'file-document-outline'
    color: string
  } => {
    if (type === 'video') return { label: 'VÍDEO', icon: 'video-outline' as const, color: '#D85A30' }
    if (type === 'audio') return { label: 'ÁUDIO', icon: 'music-note-outline' as const, color: '#3578C8' }
    return { label: 'DOCUMENTO', icon: 'file-document-outline' as const, color: '#68707A' }
  }

  const handlePreview = (attachment: ServiceOrderImageModel): void => {
    const type = getAttachmentType(attachment)
    const canPreview = canPreviewAttachment(attachment, type)
    if (!canPreview) return

    setImageView({
      uri: attachment.caminhoVisualizacao ?? attachment.caminhoArquivo,
      description: attachment.nomeArquivo,
      type,
      mimeType: attachment.mimeType,
      message: attachment.mensagemVisualizacao === '' ? undefined : attachment.mensagemVisualizacao
    })
  }

  return (
    <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
      {!!serviceOrderDetails.imagensGerais?.length &&
        <>
          <CardRow>
            <Text style={{ ...styles.pageInfo, width: '100%' }}>Anexos</Text>
            {serviceOrderDetails
              .imagensGerais
              .filter(attachment => !(/PERGUNTA\d+\.png$/i.test(attachment.nomeArquivo)))
              .map((attachment, index) => {
                const type = getAttachmentType(attachment)
                const canPreview = canPreviewAttachment(attachment, type)
                const presentation = getAttachmentPresentation(type)

                return (
                <Card
                  key={index}
                  onPress={() => { if (canPreview) handlePreview(attachment) }}
                  style={{
                    ...styles.cardBase,
                    backgroundColor: theme.colors.backgroundCard,
                    padding: isMobile ? 4 : 8,
                    ...(isPrinting && { boxShadow: 'none', breakInside: 'avoid', pageBreakInside: 'avoid' })
                  }}
                >
                  <Card.Content style={styles.cardContent}>
                    {type === 'imagem'
                      ? <Box style={styles.imageContainer}>
                          <img
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                              display: 'block'
                            }}
                            src={attachment.caminhoArquivo}
                            alt={attachment.nomeArquivo}
                          />
                      {!isPrinting &&
                        <>
                          {canPreview &&
                            <Button
                              title="Visualizar"
                              variant="text"
                              onPress={() => { handlePreview(attachment) }}
                            />
                          }
                          <Button
                            title="Baixar"
                            variant="text"
                            onPress={() => { handleDownloadFile(attachment.caminhoArquivo, attachment.nomeArquivo) }}
                          />
                        </>
                      }
                      </Box>
                      : <Box style={styles.fileContainer}>
                          <Box style={{ ...styles.fileIcon, backgroundColor: `${presentation.color}18` }}>
                            <MaterialCommunityIcons name={presentation.icon} size={52} color={presentation.color} />
                          </Box>
                          <Text variant="caption" style={{ ...styles.fileType, color: presentation.color }}>{presentation.label}</Text>
                          <Text variant="body2" numberOfLines={2} style={styles.fileName}>{attachment.nomeArquivo}</Text>
                          {!isPrinting &&
                            <Box style={styles.attachmentActions}>
                              {canPreview &&
                                <Button
                                  title="Visualizar"
                                  variant="text"
                                  onPress={() => { handlePreview(attachment) }}
                                />
                              }
                              <Button
                                title="Baixar"
                                variant="text"
                                onPress={() => { handleDownloadFile(attachment.caminhoArquivo, attachment.nomeArquivo) }}
                              />
                            </Box>
                          }
                        </Box>
                    }
                  </Card.Content>
                </Card>
                )
              })
            }
          </CardRow>
        </>
      }
    </div>
  )
}
